<script setup>
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  ref,
  watch,
} from 'vue';
import { useStore } from 'vuex';
import { useRouter, useRoute, onBeforeRouteLeave } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import ConfirmationModal from 'dashboard/components/widgets/modal/ConfirmationModal.vue';
import InboxesAPI from 'dashboard/api/inboxes';
import { usePipelinePermissions } from '../composables/usePipelinePermissions';
import { ACTIVITY_META, ACTIVITY_TYPES } from './activityTypes';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const { showAlert } = useAlert();
const { isAdmin } = usePipelinePermissions();
const currentAccountId = computed(
  () => store.getters.getCurrentAccountId ?? route.params.accountId ?? null
);
let accountGeneration = 0;
let saveGeneration = 0;
const isFormMounted = ref(false);
const normalizedAccountId = () => {
  const accountId = currentAccountId.value;
  if (accountId === undefined || accountId === null) return null;
  return String(accountId).trim() || null;
};
const openingAccountId = normalizedAccountId();
const captureAccountContext = () => ({
  accountId: openingAccountId,
  generation: accountGeneration,
});
const accountContextIsCurrent = context =>
  isFormMounted.value &&
  context.accountId !== null &&
  context.accountId === openingAccountId &&
  context.accountId === normalizedAccountId() &&
  context.generation === accountGeneration;

const editingId = computed(() => route.params.id);
const isEdit = computed(() => Boolean(editingId.value));

// ─── Activity types ───────────────────────────────────────────────────────────
// ACTIVITY_TYPES / ACTIVITY_META live in ./activityTypes.js (shared with
// SequencesIndex.vue's steps mini-preview — see activityTypes.spec.js for the
// dashboard-icons.json regression guard).

const CATEGORIES = [
  {
    key: 'activity',
    tone: 'bg-iris-3 text-iris-11',
    icon: 'flash-on',
  },
  {
    key: 'communication',
    tone: 'bg-grass-3 text-grass-11',
    icon: 'chat',
  },
  {
    key: 'integration',
    tone: 'bg-violet-3 text-violet-11',
    icon: 'arrow-swap',
  },
  {
    key: 'time',
    tone: 'bg-sand-3 text-sand-11',
    icon: 'calendar-clock',
  },
];

// Types per category for the accordion picker
const typesByCategory = computed(() =>
  CATEGORIES.reduce((map, cat) => {
    map[cat.key] = ACTIVITY_TYPES.filter(
      type =>
        ACTIVITY_META[type]?.category === cat.key &&
        (type !== 'webhook' || isAdmin.value)
    );
    return map;
  }, {})
);

const availableCategories = computed(() =>
  CATEGORIES.filter(category => typesByCategory.value[category.key]?.length)
);

// ─── Trigger types ────────────────────────────────────────────────────────────

const TRIGGER_TYPES = [
  'manual',
  'stage_change',
  'time_based',
  'condition_based',
];

const TRIGGER_META = {
  manual: { icon: 'play-circle', tone: 'bg-n-alpha-2 text-n-slate-12' },
  stage_change: { icon: 'arrow-swap', tone: 'bg-iris-3 text-iris-11' },
  time_based: {
    icon: 'calendar-clock',
    tone: 'bg-amber-3 text-amber-11',
  },
  condition_based: { icon: 'filter', tone: 'bg-violet-3 text-violet-11' },
};

// ─── Condition-based: field definitions ──────────────────────────────────────

const CONDITION_FIELDS = [
  { value: 'lead_score', labelKey: 'FIELD_LEAD_SCORE', type: 'number' },
  {
    value: 'qualification_score',
    labelKey: 'FIELD_QUALIFICATION_SCORE',
    type: 'number',
  },
  { value: 'pipeline_stage', labelKey: 'FIELD_PIPELINE_STAGE', type: 'stage' },
  {
    value: 'expected_revenue',
    labelKey: 'FIELD_EXPECTED_REVENUE',
    type: 'number',
  },
  { value: 'priority', labelKey: 'FIELD_PRIORITY', type: 'priority' },
];

const OPERATORS_NUMERIC = [
  { value: '==', labelKey: 'OPERATOR_EQ' },
  { value: '!=', labelKey: 'OPERATOR_NEQ' },
  { value: '>', labelKey: 'OPERATOR_GT' },
  { value: '<', labelKey: 'OPERATOR_LT' },
  { value: '>=', labelKey: 'OPERATOR_GTE' },
  { value: '<=', labelKey: 'OPERATOR_LTE' },
];

const OPERATORS_ENUM = [
  { value: '==', labelKey: 'OPERATOR_EQ' },
  { value: '!=', labelKey: 'OPERATOR_NEQ' },
];

const PRIORITY_OPTIONS = ['low', 'medium', 'high', 'urgent'];

// Optional fields shared by activity steps. They are not all editable in this
// form yet, but API-created definitions must survive an edit/save round-trip.
const OPTIONAL_STEP_FIELDS = [
  'duration',
  'priority',
  'schedule_hours',
  'due_days',
  'assign_to',
  'condition',
  'on_failure',
];
const STEP_TIME_LIMITS = {
  delay_days: 3650,
  delay_hours: 23,
  duration: 10080,
  schedule_hours: 87600,
  due_days: 3650,
};
const OPTIONAL_STEP_TIME_FIELDS = new Set([
  'duration',
  'schedule_hours',
  'due_days',
]);

const copyPresentStepFields = (source, target) => {
  OPTIONAL_STEP_FIELDS.forEach(field => {
    const isBlankOptionalTime =
      OPTIONAL_STEP_TIME_FIELDS.has(field) &&
      (source[field] === '' ||
        source[field] === undefined ||
        source[field] === null);
    if (
      Object.prototype.hasOwnProperty.call(source, field) &&
      !isBlankOptionalTime
    ) {
      target[field] = source[field];
    }
  });
};

const operatorsForField = field => {
  const def = CONDITION_FIELDS.find(f => f.value === field);
  if (!def) return OPERATORS_NUMERIC;
  if (def.type === 'number') return OPERATORS_NUMERIC;
  return OPERATORS_ENUM;
};

// ─── Liquid variables ─────────────────────────────────────────────────────────

const LIQUID_VARS = [
  {
    value: '{{contact.name}}',
    label:
      t('PIPELINE.SEQUENCE_DEFINITIONS.VAR_CONTACT_NAME') || 'contact.name',
  },
  {
    value: '{{contact.email}}',
    label:
      t('PIPELINE.SEQUENCE_DEFINITIONS.VAR_CONTACT_EMAIL') || 'contact.email',
  },
  {
    value: '{{contact.phone_number}}',
    label:
      t('PIPELINE.SEQUENCE_DEFINITIONS.VAR_CONTACT_PHONE') || 'contact.phone',
  },
  {
    value: '{{card.title}}',
    label: t('PIPELINE.SEQUENCE_DEFINITIONS.VAR_CARD_TITLE') || 'card.title',
  },
  {
    value: '{{card.stage}}',
    label: t('PIPELINE.SEQUENCE_DEFINITIONS.VAR_CARD_STAGE') || 'card.stage',
  },
  {
    value: '{{account.name}}',
    label:
      t('PIPELINE.SEQUENCE_DEFINITIONS.VAR_ACCOUNT_NAME') || 'account.name',
  },
  {
    value: '{{sequence.name}}',
    label:
      t('PIPELINE.SEQUENCE_DEFINITIONS.VAR_SEQUENCE_NAME') || 'sequence.name',
  },
];

// ─── Inboxes ──────────────────────────────────────────────────────────────────

const allInboxes = ref([]);

const whatsappInboxes = computed(() =>
  allInboxes.value.filter(inbox => {
    const ct = inbox.channel_type || '';
    return (
      ct === 'Channel::Whatsapp' ||
      ct === 'Channel::Waha' ||
      ct === 'Channel::Uazapi' ||
      inbox.is_waha_channel === true
    );
  })
);

// ─── Form state ───────────────────────────────────────────────────────────────

const form = reactive({
  name: '',
  description: '',
  trigger_type: 'manual',
  trigger_conditions: {},
  active: true,
  steps: [],
});
const isHydratingForm = ref(false);
const initialSnapshot = ref(null);
const discardConfirmationRef = ref(null);
let discardConfirmationPromise = null;
const captureSnapshot = () => JSON.stringify(form);
const isDirty = computed(
  () =>
    initialSnapshot.value !== null &&
    captureSnapshot() !== initialSnapshot.value
);

// ─── Pipelines / stages ───────────────────────────────────────────────────────

const allPipelines = computed(
  () => store.getters['pipelines/getPipelines'] || []
);

// Flat sorted stages for a given pipeline id (or all pipelines merged)
const stagesForPipeline = computed(() => {
  const pipelineId = form.trigger_conditions?.funnel_id;
  if (!pipelineId) return [];
  const pipelines = allPipelines.value.filter(
    p => String(p.id) === String(pipelineId)
  );

  const stageMap = {};
  pipelines.forEach(pipeline => {
    const stages = pipeline.stages || {};
    Object.entries(stages).forEach(([id, stage]) => {
      stageMap[id] = { id, name: stage.name || stage.title || id };
    });
  });

  return Object.values(stageMap).sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );
});

const stageEditorOptions = computed(() => {
  const options = [...stagesForPipeline.value];
  const knownIds = new Set(options.map(stage => String(stage.id)));
  [form.trigger_conditions?.from_stage_id, form.trigger_conditions?.to_stage_id]
    .filter(value => value !== undefined && value !== null && value !== '')
    .forEach(value => {
      if (knownIds.has(String(value))) return;
      options.push({ id: value, name: String(value) });
      knownIds.add(String(value));
    });
  return options;
});

// All stages merged across all pipelines (used for condition_based stage picker)
const allStages = computed(() => {
  const stageMap = {};
  allPipelines.value.forEach(pipeline => {
    const stages = pipeline.stages || {};
    Object.entries(stages).forEach(([id, stage]) => {
      stageMap[id] = { id, name: stage.name || stage.title || id };
    });
  });
  return Object.values(stageMap).sort((a, b) =>
    (a.name || '').localeCompare(b.name || '')
  );
});

// Time-based eligibility filter stages (same as allStages but explicit)
const eligibilityStages = computed(() => allStages.value);

// time_based mode toggle state
const timeBasedMode = ref('every_n_days');
const MAX_DAYS_IN_STAGE = 36500;
const MAX_CRON_LENGTH = 255;
const LEGACY_DAILY_TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;
const CRON_FIELD_RANGES = [
  [0, 59],
  [0, 23],
  [1, 31],
  [1, 12],
  [0, 7],
];

const isValidCronPart = (part, minimum, maximum) => {
  const range = part.match(/^(\d+)-(\d+)$/);
  if (range) {
    const first = Number(range[1]);
    const last = Number(range[2]);
    return (
      first >= minimum &&
      first <= maximum &&
      last >= minimum &&
      last <= maximum &&
      first <= last
    );
  }
  return (
    /^\d+$/.test(part) && Number(part) >= minimum && Number(part) <= maximum
  );
};

const isValidCronField = (field, minimum, maximum) => {
  if (field === '*') return true;
  const step = field.match(/^\*\/(\d+)$/);
  if (step) {
    const value = Number(step[1]);
    return value > 0 && value <= maximum - minimum + 1;
  }
  return field
    .split(',')
    .every(part => isValidCronPart(part, minimum, maximum));
};

const isValidFiveFieldCron = value => {
  if (typeof value !== 'string' || value.length > MAX_CRON_LENGTH) return false;
  const fields = value.trim() ? value.trim().split(/\s+/) : [];
  return (
    fields.length === CRON_FIELD_RANGES.length &&
    fields.every((field, index) =>
      isValidCronField(field, ...CRON_FIELD_RANGES[index])
    )
  );
};

const readStageCondition = (conditions, canonicalKey, legacyKey) => {
  if (Object.prototype.hasOwnProperty.call(conditions, canonicalKey)) {
    return conditions[canonicalKey];
  }
  return conditions[legacyKey];
};

const pipelineContainsStages = (pipeline, stageIds) => {
  const pipelineStageIds = new Set(
    Object.keys(pipeline?.stages || {}).map(String)
  );
  return stageIds.every(stageId => pipelineStageIds.has(String(stageId)));
};

const normalizeStageChangeConditions = rawConditions => {
  const conditions =
    rawConditions && typeof rawConditions === 'object'
      ? { ...rawConditions }
      : {};
  const fromStageId = readStageCondition(
    conditions,
    'from_stage_id',
    'from_stage'
  );
  const toStageId = readStageCondition(conditions, 'to_stage_id', 'to_stage');

  delete conditions.from_stage;
  delete conditions.to_stage;
  if (fromStageId !== undefined) conditions.from_stage_id = fromStageId;
  if (toStageId !== undefined) conditions.to_stage_id = toStageId;

  const savedStageIds = [fromStageId, toStageId].filter(
    value => value !== undefined && value !== null && value !== ''
  );
  if (!conditions.funnel_id && savedStageIds.length) {
    const candidates = allPipelines.value.filter(pipeline =>
      pipelineContainsStages(pipeline, savedStageIds)
    );
    if (candidates.length === 1) conditions.funnel_id = candidates[0].id;
  }

  return conditions;
};

const legacyDailyCron = conditions => {
  if (
    conditions?.schedule !== 'daily' ||
    !LEGACY_DAILY_TIME_PATTERN.test(conditions?.time || '')
  ) {
    return null;
  }

  const [hour, minute] = conditions.time.split(':').map(Number);
  return `${minute} ${hour} * * *`;
};

// Ensure eligibility_filter sub-object exists before binding to its properties
const ensureEligibilityFilter = () => {
  if (!form.trigger_conditions.eligibility_filter) {
    form.trigger_conditions.eligibility_filter = {
      stage_id: '',
      days_in_stage: '',
    };
  }
};

const errors = ref({});
const showTimeFilters = ref(false);

// Reset trigger_conditions when the trigger type changes
watch(
  () => form.trigger_type,
  () => {
    if (isHydratingForm.value) return;
    form.trigger_conditions = {};
    timeBasedMode.value = 'every_n_days';
    showTimeFilters.value = false;
  },
  { flush: 'sync' }
);

watch(
  () => form.trigger_conditions?.funnel_id,
  (funnelId, previousFunnelId) => {
    if (isHydratingForm.value || form.trigger_type !== 'stage_change') return;
    if (String(funnelId || '') === String(previousFunnelId || '')) return;
    form.trigger_conditions.from_stage_id = '';
    form.trigger_conditions.to_stage_id = '';
    delete form.trigger_conditions.from_stage;
    delete form.trigger_conditions.to_stage;
  }
);

// Keep timeBasedMode in sync with trigger_conditions
watch(timeBasedMode, mode => {
  if (mode === 'every_n_days') {
    delete form.trigger_conditions.cron_expression;
  } else {
    delete form.trigger_conditions.every_n_days;
  }
});

// Per-step accordion: track which category is open per step index
const openCategoryPerStep = ref({});

// Refs for textareas (indexed by step idx + field name)
const textareaRefs = ref({});

const isSaving = computed(
  () => store.getters['pipelineSequenceDefinitions/isSavingSequenceDefinition']
);

// ─── Computed helpers ─────────────────────────────────────────────────────────

const totalDuration = computed(() => {
  return form.steps.reduce(
    (acc, s) =>
      acc + (Number(s.delay_days) || 0) + (Number(s.delay_hours) || 0) / 24,
    0
  );
});

const totalDurationLabel = computed(() => {
  const days = Math.round(totalDuration.value);
  return `${days}d`;
});

// ─── Category accordion helpers ───────────────────────────────────────────────

const toggleCategory = (stepIdx, catKey) => {
  const current = openCategoryPerStep.value[stepIdx];
  openCategoryPerStep.value = {
    ...openCategoryPerStep.value,
    [stepIdx]: current === catKey ? null : catKey,
  };
};

const openCategoryForStep = stepIdx => openCategoryPerStep.value[stepIdx];

const hasWebhookSteps = steps =>
  Array.isArray(steps) && steps.some(step => step?.activity_type === 'webhook');

// ─── Type-specific field initialization ──────────────────────────────────────

const initTypeFields = (step, type) => {
  if (type === 'webhook') {
    if (!step.webhook_url) step.webhook_url = '';
    if (!step.webhook_method) step.webhook_method = 'POST';
    if (!step.webhook_headers) step.webhook_headers = [];
    if (!step.webhook_payload_template) step.webhook_payload_template = '';
    if (!step.on_failure) step.on_failure = 'continue';
  } else if (type === 'whatsapp_message') {
    if (!step.message_template) step.message_template = '';
    if (!step.inbox_id) step.inbox_id = '';
    if (!step.on_failure) step.on_failure = 'continue';
  } else if (type === 'whatsapp_media') {
    if (!step.attachment_type) step.attachment_type = '';
    if (!step.attachment_url) step.attachment_url = '';
    if (!step.caption) step.caption = '';
    if (!step.inbox_id) step.inbox_id = '';
    if (!step.on_failure) step.on_failure = 'continue';
  }
};

const selectType = (stepIdx, type) => {
  if (type === 'webhook' && !isAdmin.value) return;

  const step = form.steps[stepIdx];
  step.activity_type = type;
  // Close the accordion after selection
  openCategoryPerStep.value = {
    ...openCategoryPerStep.value,
    [stepIdx]: null,
  };
  // Reset type-specific fields
  initTypeFields(step, type);
};

// ─── Webhook header helpers ───────────────────────────────────────────────────

const addHeader = step => {
  if (!step.webhook_headers) step.webhook_headers = [];
  if (step.webhook_headers.length < 5) {
    step.webhook_headers.push({ key: '', value: '' });
  }
};

const removeHeader = (step, idx) => {
  step.webhook_headers.splice(idx, 1);
};

// ─── Liquid variable chip insertion ──────────────────────────────────────────

const insertLiquidVar = (stepIdx, field, varValue) => {
  const refKey = `${stepIdx}_${field}`;
  const el = textareaRefs.value[refKey];
  const step = form.steps[stepIdx];
  if (el) {
    const start = el.selectionStart ?? (step[field] || '').length;
    const end = el.selectionEnd ?? start;
    const current = step[field] || '';
    step[field] = current.slice(0, start) + varValue + current.slice(end);
    // Restore focus + caret after Vue updates DOM
    setTimeout(() => {
      el.focus();
      const pos = start + varValue.length;
      el.setSelectionRange(pos, pos);
    }, 0);
  } else {
    step[field] = (step[field] || '') + varValue;
  }
};

const setTextareaRef = (stepIdx, field, el) => {
  const refKey = `${stepIdx}_${field}`;
  if (el) {
    textareaRefs.value[refKey] = el;
  } else {
    delete textareaRefs.value[refKey];
  }
};

// ─── Validation ───────────────────────────────────────────────────────────────

const isValidUrl = url => {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
};

const validate = () => {
  errors.value = {};
  if (!form.name?.trim()) {
    errors.value.name = t(
      'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_NAME_REQUIRED'
    );
  }
  if (!form.steps.length) {
    errors.value.steps = t(
      'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_STEPS_REQUIRED'
    );
  }

  // Trigger-type specific validation
  if (form.trigger_type === 'stage_change') {
    const tc = form.trigger_conditions || {};
    const fromStageId = readStageCondition(tc, 'from_stage_id', 'from_stage');
    const toStageId = readStageCondition(tc, 'to_stage_id', 'to_stage');
    const validStageIds = new Set(
      stagesForPipeline.value.map(stage => String(stage.id))
    );
    if (!tc.funnel_id) {
      errors.value.trigger_conditions = t(
        'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_TRIGGER_PIPELINE_REQUIRED'
      );
    } else if (
      !toStageId ||
      !validStageIds.has(String(toStageId)) ||
      (fromStageId && !validStageIds.has(String(fromStageId)))
    ) {
      errors.value.trigger_conditions = t(
        'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_TRIGGER_STAGE_TO_REQUIRED'
      );
    }
  } else if (form.trigger_type === 'time_based') {
    const tc = form.trigger_conditions || {};
    const hasEveryN =
      tc.every_n_days !== undefined &&
      tc.every_n_days !== '' &&
      Number.isInteger(Number(tc.every_n_days)) &&
      Number(tc.every_n_days) >= 1 &&
      Number(tc.every_n_days) <= 365;
    const hasCron = isValidFiveFieldCron(tc.cron_expression);
    const filterDays = tc.eligibility_filter?.days_in_stage;
    const hasInvalidFilterDays =
      filterDays !== undefined &&
      filterDays !== '' &&
      (!Number.isInteger(Number(filterDays)) ||
        Number(filterDays) < 0 ||
        Number(filterDays) > MAX_DAYS_IN_STAGE);
    const hasInvalidCadence =
      timeBasedMode.value === 'every_n_days' ? !hasEveryN : !hasCron;

    if (hasInvalidCadence) {
      errors.value.trigger_conditions =
        timeBasedMode.value === 'cron_expression'
          ? t(
              'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_TRIGGER_TIME_CRON_INVALID'
            )
          : t('PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_TRIGGER_TIME_REQUIRED');
    } else if (hasInvalidFilterDays) {
      errors.value.trigger_days_in_stage = t(
        'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_TRIGGER_TIME_FILTER_DAYS_INVALID'
      );
      showTimeFilters.value = true;
      ensureEligibilityFilter();
    }
  } else if (form.trigger_type === 'condition_based') {
    const tc = form.trigger_conditions || {};
    if (
      !tc.field ||
      !tc.operator ||
      tc.value === undefined ||
      tc.value === ''
    ) {
      errors.value.trigger_conditions = t(
        'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_TRIGGER_CONDITION_REQUIRED'
      );
    }
  }
  form.steps.forEach((step, idx) => {
    Object.entries(STEP_TIME_LIMITS).forEach(([field, maximum]) => {
      const value = step[field];
      const isOptionalAndBlank =
        OPTIONAL_STEP_TIME_FIELDS.has(field) &&
        (value === '' || value === undefined || value === null);
      if (isOptionalAndBlank) return;

      const numericValue = value === '' ? 0 : Number(value);
      if (
        !Number.isInteger(numericValue) ||
        numericValue < 0 ||
        numericValue > maximum
      ) {
        errors.value[`step_${idx}_${field}`] = t(
          'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_STEP_TIME_RANGE',
          { min: 0, max: maximum }
        );
      }
    });
    if (!step.title?.trim()) {
      errors.value[`step_${idx}_title`] = t(
        'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_STEP_TITLE_REQUIRED'
      );
    }
    if (!step.activity_type) {
      errors.value[`step_${idx}_type`] = t(
        'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_STEP_TYPE_REQUIRED'
      );
    }
    // Type-specific validation
    if (step.activity_type === 'webhook') {
      if (!step.webhook_url?.trim()) {
        errors.value[`step_${idx}_webhook_url`] = t(
          'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_WEBHOOK_URL_REQUIRED'
        );
      } else if (!isValidUrl(step.webhook_url.trim())) {
        errors.value[`step_${idx}_webhook_url`] = t(
          'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_WEBHOOK_URL_INVALID'
        );
      }
    }
    if (step.activity_type === 'whatsapp_message') {
      if (!step.message_template?.trim()) {
        errors.value[`step_${idx}_message_template`] = t(
          'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_MESSAGE_REQUIRED'
        );
      }
    }
    if (step.activity_type === 'whatsapp_media') {
      if (!step.attachment_type) {
        errors.value[`step_${idx}_attachment_type`] = t(
          'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_ATTACHMENT_TYPE_REQUIRED'
        );
      }
      if (!step.attachment_url?.trim()) {
        errors.value[`step_${idx}_attachment_url`] = t(
          'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_ATTACHMENT_URL_REQUIRED'
        );
      } else if (!isValidUrl(step.attachment_url.trim())) {
        errors.value[`step_${idx}_attachment_url`] = t(
          'PIPELINE.SEQUENCE_DEFINITIONS.VALIDATION_WEBHOOK_URL_INVALID'
        );
      }
    }
  });
  return Object.keys(errors.value).length === 0;
};

// ─── Step helpers ─────────────────────────────────────────────────────────────

const blankStep = () => ({
  step_number: form.steps.length + 1,
  activity_type: 'call',
  title: '',
  description: '',
  delay_days: 0,
  delay_hours: 0,
});

const addStep = () => {
  form.steps.push(blankStep());
};

const removeStep = idx => {
  form.steps.splice(idx, 1);
  form.steps.forEach((s, i) => {
    s.step_number = i + 1;
  });
  // Clean up category accordion state for this step
  const newState = { ...openCategoryPerStep.value };
  delete newState[idx];
  openCategoryPerStep.value = newState;
};

const moveStep = (idx, dir) => {
  const target = idx + dir;
  if (target < 0 || target >= form.steps.length) return;
  const [item] = form.steps.splice(idx, 1);
  form.steps.splice(target, 0, item);
  form.steps.forEach((s, i) => {
    s.step_number = i + 1;
  });
};

// ─── Navigation ───────────────────────────────────────────────────────────────

const confirmDiscardChanges = () => {
  if (!discardConfirmationRef.value) return Promise.resolve(false);
  if (!discardConfirmationPromise) {
    discardConfirmationPromise = discardConfirmationRef.value
      .showConfirmation()
      .finally(() => {
        discardConfirmationPromise = null;
      });
  }
  return discardConfirmationPromise;
};

const goBack = async ({ skipConfirmation = false } = {}) => {
  if (!skipConfirmation && isDirty.value) {
    const shouldLeave = await confirmDiscardChanges();
    if (!shouldLeave) return;
  }
  initialSnapshot.value = captureSnapshot();
  await router.push({ name: 'pipeline_sequences' });
};

const handleBeforeUnload = event => {
  if (!isDirty.value) return;
  event.preventDefault();
  // eslint-disable-next-line no-param-reassign
  event.returnValue = '';
};

onBeforeRouteLeave(async (_to, _from, next) => {
  if (!isDirty.value) {
    next();
    return;
  }
  const shouldLeave = await confirmDiscardChanges();
  next(shouldLeave);
});

// ─── Save ─────────────────────────────────────────────────────────────────────

const buildWebhookHeaderPayload = header => {
  const key = header.key?.trim();
  const value = header.value?.trim();
  if (!key) return null;
  if (value) return { key, value };
  if (!header.configured || !header.credential_ref) return null;

  return {
    key,
    configured: true,
    credential_ref: header.credential_ref,
  };
};

const buildStepPayload = (s, i) => {
  const base = {
    step_number: i + 1,
    activity_type: s.activity_type,
    title: s.title.trim(),
    description: s.description?.trim() || '',
    delay_days: Number(s.delay_days) || 0,
    delay_hours: Number(s.delay_hours) || 0,
  };
  copyPresentStepFields(s, base);

  if (s.activity_type === 'webhook') {
    return {
      ...base,
      webhook_url: s.webhook_url?.trim() || '',
      webhook_method: s.webhook_method || 'POST',
      webhook_headers: (s.webhook_headers || [])
        .map(buildWebhookHeaderPayload)
        .filter(Boolean),
      webhook_payload_template: s.webhook_payload_template?.trim() || '',
      on_failure: s.on_failure || 'continue',
    };
  }
  if (s.activity_type === 'whatsapp_message') {
    return {
      ...base,
      message_template: s.message_template?.trim() || '',
      inbox_id: s.inbox_id || null,
      on_failure: s.on_failure || 'continue',
    };
  }
  if (s.activity_type === 'whatsapp_media') {
    return {
      ...base,
      attachment_type: s.attachment_type || '',
      attachment_url: s.attachment_url?.trim() || '',
      caption: s.caption?.trim() || '',
      inbox_id: s.inbox_id || null,
      on_failure: s.on_failure || 'continue',
    };
  }
  return base;
};

const buildTriggerConditions = () => {
  const tc = form.trigger_conditions || {};
  if (form.trigger_type === 'manual') return {};

  if (form.trigger_type === 'stage_change') {
    const result = {};
    const fromStageId = readStageCondition(tc, 'from_stage_id', 'from_stage');
    const toStageId = readStageCondition(tc, 'to_stage_id', 'to_stage');
    if (fromStageId) result.from_stage_id = fromStageId;
    if (toStageId) result.to_stage_id = toStageId;
    if (tc.funnel_id) result.funnel_id = tc.funnel_id;
    return result;
  }

  if (form.trigger_type === 'time_based') {
    const result = {};
    if (
      timeBasedMode.value === 'cron_expression' &&
      tc.cron_expression?.trim()
    ) {
      result.cron_expression = tc.cron_expression.trim();
    } else if (tc.every_n_days) {
      result.every_n_days = Number(tc.every_n_days);
    }
    const filter = {};
    if (tc.eligibility_filter?.stage_id) {
      filter.stage_id = tc.eligibility_filter.stage_id;
    }
    if (
      tc.eligibility_filter?.days_in_stage !== undefined &&
      tc.eligibility_filter?.days_in_stage !== ''
    ) {
      filter.days_in_stage = Number(tc.eligibility_filter.days_in_stage);
    }
    if (Object.keys(filter).length) result.eligibility_filter = filter;
    return result;
  }

  if (form.trigger_type === 'condition_based') {
    return {
      field: tc.field || '',
      operator: tc.operator || '',
      value: tc.value !== undefined ? tc.value : '',
      conjunction: tc.conjunction || 'and',
    };
  }

  return {};
};

const handleSave = async () => {
  if (!validate()) return;
  const accountContext = captureAccountContext();
  if (accountContext.accountId === null) return;
  saveGeneration += 1;
  const requestGeneration = saveGeneration;
  const requestIsCurrent = () =>
    requestGeneration === saveGeneration &&
    accountContextIsCurrent(accountContext);

  const payload = {
    name: form.name.trim(),
    description: form.description?.trim() || null,
    trigger_type: form.trigger_type,
    trigger_conditions: buildTriggerConditions(),
    active: form.active,
    steps: form.steps.map(buildStepPayload),
  };

  try {
    if (isEdit.value) {
      await store.dispatch('pipelineSequenceDefinitions/update', {
        id: editingId.value,
        payload,
        accountId: accountContext.accountId,
      });
    } else {
      await store.dispatch('pipelineSequenceDefinitions/create', {
        ...payload,
        accountId: accountContext.accountId,
      });
    }
    if (!requestIsCurrent()) return;
    showAlert(t('PIPELINE.SEQUENCE_DEFINITIONS.SAVE_SUCCESS'));
    await goBack({ skipConfirmation: true });
  } catch {
    if (!requestIsCurrent()) return;
    const errorMsg =
      store.getters[
        'pipelineSequenceDefinitions/getSequenceDefinitionsError'
      ] || t('PIPELINE.SEQUENCE_DEFINITIONS.SAVE_ERROR');
    showAlert(errorMsg);
  }
};

// ─── Mount ────────────────────────────────────────────────────────────────────

onMounted(async () => {
  isFormMounted.value = true;
  window.addEventListener('beforeunload', handleBeforeUnload);
  const accountContext = captureAccountContext();
  if (accountContext.accountId === null) return;

  const [inboxesResult] = await Promise.allSettled([
    InboxesAPI.getForAccount(accountContext.accountId),
    store.dispatch('pipelines/fetch', {
      accountId: accountContext.accountId,
    }),
  ]);
  if (!accountContextIsCurrent(accountContext)) return;
  allInboxes.value = Array.isArray(inboxesResult.value?.data?.payload)
    ? inboxesResult.value.data.payload
    : [];

  if (isEdit.value) {
    const record = await store.dispatch(
      'pipelineSequenceDefinitions/fetchOne',
      {
        id: editingId.value,
        accountId: accountContext.accountId,
      }
    );
    if (!accountContextIsCurrent(accountContext)) return;
    if (record) {
      if (hasWebhookSteps(record.steps) && !isAdmin.value) {
        showAlert(t('PIPELINE.SEQUENCE_DEFINITIONS.WEBHOOK_ADMIN_REQUIRED'));
        await router.push({ name: 'pipeline_sequences' });
        return;
      }

      isHydratingForm.value = true;
      const steps = (record.steps || []).map((s, i) => {
        const step = {
          step_number: i + 1,
          activity_type: s.activity_type || 'call',
          title: s.title || '',
          description: s.description || '',
          delay_days: Number(s.delay_days) || 0,
          delay_hours: Number(s.delay_hours) || 0,
        };
        copyPresentStepFields(s, step);
        initTypeFields(step, step.activity_type);
        // Restore type-specific fields from saved data
        if (s.activity_type === 'webhook') {
          step.webhook_url = s.webhook_url || '';
          step.webhook_method = s.webhook_method || 'POST';
          step.webhook_headers = Array.isArray(s.webhook_headers)
            ? s.webhook_headers.map(header => ({ ...header }))
            : [];
          step.webhook_payload_template = s.webhook_payload_template || '';
          step.on_failure = s.on_failure || 'continue';
        } else if (s.activity_type === 'whatsapp_message') {
          step.message_template = s.message_template || '';
          step.inbox_id = s.inbox_id || '';
          step.on_failure = s.on_failure || 'continue';
        } else if (s.activity_type === 'whatsapp_media') {
          step.attachment_type = s.attachment_type || '';
          step.attachment_url = s.attachment_url || '';
          step.caption = s.caption || '';
          step.inbox_id = s.inbox_id || '';
          step.on_failure = s.on_failure || 'continue';
        }
        return step;
      });

      // Restore trigger_conditions and re-derive timeBasedMode
      const savedTc = record.trigger_conditions || {};
      const savedType = record.trigger_type || 'manual';
      let restoredTc = { ...savedTc };
      if (savedType === 'stage_change') {
        restoredTc = normalizeStageChangeConditions(savedTc);
      }
      const canonicalLegacyCron = legacyDailyCron(savedTc);
      if (savedType === 'time_based' && canonicalLegacyCron) {
        restoredTc.cron_expression = canonicalLegacyCron;
        delete restoredTc.schedule;
        delete restoredTc.time;
      }
      if (savedType === 'time_based' && restoredTc.cron_expression) {
        timeBasedMode.value = 'cron_expression';
      }

      // Ensure eligibility_filter sub-object exists for time_based
      if (savedType === 'time_based' && !restoredTc.eligibility_filter) {
        restoredTc.eligibility_filter = { stage_id: '', days_in_stage: '' };
      }

      Object.assign(form, {
        name: record.name || '',
        description: record.description || '',
        trigger_type: savedType,
        trigger_conditions: restoredTc,
        active: record.active !== false,
        steps,
      });
      // Default-flush watchers created by Object.assign must observe the
      // hydration guard before normal user-driven resets are enabled.
      await nextTick();
      isHydratingForm.value = false;
    }
  } else if (!form.steps.length) {
    addStep();
  }
  initialSnapshot.value = captureSnapshot();
});

watch(
  currentAccountId,
  async (accountId, previousAccountId) => {
    if (String(accountId ?? '') === String(previousAccountId ?? '')) return;
    accountGeneration += 1;
    saveGeneration += 1;
    allInboxes.value = [];
    errors.value = {};
    openCategoryPerStep.value = {};
    textareaRefs.value = {};
    isHydratingForm.value = true;
    Object.assign(form, {
      name: '',
      description: '',
      trigger_type: 'manual',
      trigger_conditions: {},
      active: true,
      steps: [],
    });
    addStep();
    timeBasedMode.value = 'every_n_days';
    showTimeFilters.value = false;
    initialSnapshot.value = captureSnapshot();
    const resetRequests = Promise.all([
      store.dispatch('pipelineSequenceDefinitions/reset'),
      store.dispatch('pipelines/reset'),
    ]);
    await nextTick();
    isHydratingForm.value = false;
    await resetRequests;
    const nextAccountId = normalizedAccountId();
    if (!isFormMounted.value || nextAccountId === null) return;
    await router.push({
      name: 'pipeline_sequences',
      params: { accountId: nextAccountId },
    });
  },
  { flush: 'sync' }
);

onUnmounted(() => {
  isFormMounted.value = false;
  accountGeneration += 1;
  saveGeneration += 1;
  allInboxes.value = [];
  window.removeEventListener('beforeunload', handleBeforeUnload);
});
</script>

<template>
  <div class="flex flex-col flex-1 w-full min-h-0 bg-n-background">
    <!-- Header -->
    <header
      class="sticky top-0 z-10 flex flex-col items-stretch justify-between gap-4 border-b border-n-weak bg-n-solid-1 px-4 py-4 sm:flex-row sm:items-center sm:px-8 sm:py-5"
    >
      <div class="flex items-center gap-4 min-w-0">
        <button
          type="button"
          class="flex size-11 flex-shrink-0 items-center justify-center rounded-xl text-n-slate-11 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          :aria-label="t('PIPELINE.SEQUENCE_DEFINITIONS.BTN_BACK_TO_LIST')"
          @click="goBack"
        >
          <FluentIcon icon="chevron-left" size="20" />
        </button>
        <div
          class="flex items-center justify-center w-12 h-12 rounded-xl bg-gradient-to-br from-n-brand to-iris-9 text-white shadow-sm flex-shrink-0"
        >
          <FluentIcon icon="flash-on" size="22" />
        </div>
        <div class="min-w-0">
          <h1 class="text-xl font-semibold text-n-slate-12 truncate">
            {{
              isEdit
                ? t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_TITLE_EDIT')
                : t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_TITLE_NEW')
            }}
          </h1>
          <p class="text-sm text-n-slate-10 mt-0.5 truncate">
            {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_SUBTITLE') }}
          </p>
        </div>
      </div>
      <div class="flex flex-shrink-0 items-center justify-end gap-2">
        <button
          type="button"
          class="min-h-11 rounded-lg px-4 py-2 text-sm font-medium text-n-slate-11 transition-colors hover:bg-n-alpha-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
          @click="goBack"
        >
          {{ t('PIPELINE.SEQUENCE_DEFINITIONS.BTN_CANCEL') }}
        </button>
        <button
          type="button"
          :disabled="isSaving"
          class="inline-flex min-h-11 items-center gap-2 rounded-lg bg-n-brand px-5 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-n-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-60"
          @click="handleSave"
        >
          <FluentIcon v-if="!isSaving" icon="save" size="14" />
          {{
            isSaving
              ? t('PIPELINE.SEQUENCE_DEFINITIONS.BTN_SAVING')
              : t('PIPELINE.SEQUENCE_DEFINITIONS.BTN_SAVE')
          }}
        </button>
      </div>
    </header>

    <!-- Body -->
    <div class="flex-1 overflow-auto">
      <div class="mx-auto max-w-4xl space-y-6 px-4 py-6 sm:px-8 sm:py-8">
        <!-- General -->
        <section
          class="bg-n-solid-1 border border-n-weak rounded-2xl overflow-hidden"
        >
          <div
            class="px-6 py-4 border-b border-n-weak bg-n-alpha-1 flex items-center gap-3"
          >
            <div
              class="flex items-center justify-center w-9 h-9 rounded-lg bg-n-brand/10 text-n-brand"
            >
              <FluentIcon icon="info" size="16" />
            </div>
            <div>
              <h2 class="text-base font-semibold text-n-slate-12">
                {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_GENERAL_TITLE') }}
              </h2>
              <p class="text-xs text-n-slate-10 mt-0.5">
                {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_GENERAL_HINT') }}
              </p>
            </div>
          </div>
          <div class="p-6 space-y-5">
            <div>
              <label
                for="sequence-name"
                class="block text-sm font-medium text-n-slate-12 mb-1.5"
              >
                {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_NAME_LABEL') }}
                <span class="text-ruby-11">*</span>
              </label>
              <input
                id="sequence-name"
                v-model="form.name"
                name="sequence-name"
                type="text"
                autocomplete="off"
                class="w-full px-3.5 py-2.5 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors"
                :placeholder="
                  t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_NAME_PLACEHOLDER')
                "
              />
              <p
                v-if="errors.name"
                class="text-xs text-ruby-11 mt-1.5 flex items-center gap-1"
              >
                <FluentIcon icon="error-circle" size="12" />
                {{ errors.name }}
              </p>
            </div>

            <div>
              <label
                for="sequence-description"
                class="block text-sm font-medium text-n-slate-12 mb-1.5"
              >
                {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_DESCRIPTION_LABEL') }}
              </label>
              <textarea
                id="sequence-description"
                v-model="form.description"
                name="sequence-description"
                rows="2"
                class="w-full px-3.5 py-2.5 rounded-lg border border-n-weak bg-n-solid-2 text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors resize-y"
                :placeholder="
                  t(
                    'PIPELINE.SEQUENCE_DEFINITIONS.FORM_DESCRIPTION_PLACEHOLDER'
                  )
                "
              />
            </div>

            <div>
              <p
                id="sequence-trigger-type-label"
                class="block text-sm font-medium text-n-slate-12 mb-1.5"
              >
                {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_TRIGGER_LABEL') }}
              </p>
              <div
                class="grid grid-cols-2 sm:grid-cols-4 gap-2"
                role="group"
                aria-labelledby="sequence-trigger-type-label"
              >
                <button
                  v-for="type in TRIGGER_TYPES"
                  :key="type"
                  type="button"
                  class="flex min-h-11 items-center gap-2 rounded-lg border px-3 py-2.5 text-left transition-[border-color,background-color,color,box-shadow] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                  :aria-pressed="form.trigger_type === type"
                  :class="
                    form.trigger_type === type
                      ? 'border-n-brand bg-n-brand/10 text-n-slate-12 ring-2 ring-n-brand/20'
                      : 'border-n-weak bg-n-solid-2 text-n-slate-11 hover:border-n-strong'
                  "
                  @click="form.trigger_type = type"
                >
                  <span
                    class="flex items-center justify-center w-7 h-7 rounded-md flex-shrink-0"
                    :class="
                      TRIGGER_META[type]?.tone || 'bg-n-alpha-2 text-n-slate-11'
                    "
                  >
                    <FluentIcon
                      :icon="TRIGGER_META[type]?.icon || 'add-circle'"
                      size="13"
                    />
                  </span>
                  <span class="text-xs font-medium truncate">
                    {{
                      t(
                        `PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_${type.toUpperCase()}`
                      )
                    }}
                  </span>
                </button>
              </div>
            </div>

            <!-- ── Trigger configuration sub-form ─────────────────────── -->
            <div
              class="rounded-xl border border-n-weak bg-n-alpha-1 p-4 space-y-4"
            >
              <p
                class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
              >
                {{ t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_CONFIG_TITLE') }}
              </p>

              <!-- manual -->
              <template v-if="form.trigger_type === 'manual'">
                <p class="text-sm text-n-slate-11 flex items-center gap-2">
                  <span
                    class="flex items-center justify-center w-6 h-6 rounded-md bg-n-alpha-2 text-n-slate-11 flex-shrink-0"
                  >
                    <FluentIcon icon="play-circle" size="13" />
                  </span>
                  {{ t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_MANUAL_HINT') }}
                </p>
              </template>

              <!-- stage_change -->
              <template v-else-if="form.trigger_type === 'stage_change'">
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <!-- Pipeline must be chosen before its dependent stages. -->
                  <div>
                    <label
                      for="sequence-trigger-pipeline"
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{
                        t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_STAGE_FUNNEL')
                      }}
                      <span class="text-ruby-11">*</span>
                    </label>
                    <select
                      id="sequence-trigger-pipeline"
                      v-model="form.trigger_conditions.funnel_id"
                      name="sequence-trigger-pipeline"
                      class="!mb-0 min-h-11 w-full rounded-md border bg-n-solid-2 py-2 pl-3 pr-10 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                      :class="
                        errors.trigger_conditions &&
                        !form.trigger_conditions.funnel_id
                          ? 'border-ruby-8'
                          : 'border-n-weak'
                      "
                    >
                      <option value="" disabled>
                        {{
                          t(
                            'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_SELECT_PIPELINE'
                          )
                        }}
                      </option>
                      <option
                        v-for="pipeline in allPipelines"
                        :key="pipeline.id"
                        :value="pipeline.id"
                      >
                        {{ pipeline.name || pipeline.title }}
                      </option>
                    </select>
                  </div>

                  <!-- From stage -->
                  <div>
                    <label
                      for="sequence-trigger-from-stage"
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{
                        t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_STAGE_FROM')
                      }}
                    </label>
                    <select
                      id="sequence-trigger-from-stage"
                      v-model="form.trigger_conditions.from_stage_id"
                      name="sequence-trigger-from-stage"
                      :disabled="!form.trigger_conditions.funnel_id"
                      class="!mb-0 min-h-11 w-full rounded-md border border-n-weak bg-n-solid-2 py-2 pl-3 pr-10 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20 disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      <option value="">
                        {{
                          form.trigger_conditions.funnel_id
                            ? t(
                                'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_STAGE_ANY'
                              )
                            : t(
                                'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_SELECT_PIPELINE_FIRST'
                              )
                        }}
                      </option>
                      <option
                        v-for="stage in stageEditorOptions"
                        :key="stage.id"
                        :value="stage.id"
                      >
                        {{ stage.name }}
                      </option>
                    </select>
                  </div>

                  <!-- To stage -->
                  <div>
                    <label
                      for="sequence-trigger-to-stage"
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{ t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_STAGE_TO') }}
                      <span class="text-ruby-11">*</span>
                    </label>
                    <select
                      id="sequence-trigger-to-stage"
                      v-model="form.trigger_conditions.to_stage_id"
                      name="sequence-trigger-to-stage"
                      :disabled="!form.trigger_conditions.funnel_id"
                      class="!mb-0 min-h-11 w-full rounded-md border bg-n-solid-2 py-2 pl-3 pr-10 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20 disabled:cursor-not-allowed disabled:opacity-60"
                      :class="
                        errors.trigger_conditions
                          ? 'border-ruby-8'
                          : 'border-n-weak'
                      "
                    >
                      <option value="">
                        {{
                          form.trigger_conditions.funnel_id
                            ? t(
                                'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_STAGE_ANY'
                              )
                            : t(
                                'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_SELECT_PIPELINE_FIRST'
                              )
                        }}
                      </option>
                      <option
                        v-for="stage in stageEditorOptions"
                        :key="stage.id"
                        :value="stage.id"
                      >
                        {{ stage.name }}
                      </option>
                    </select>
                  </div>
                </div>

                <p
                  v-if="errors.trigger_conditions"
                  class="text-xs text-ruby-11 flex items-center gap-1"
                >
                  <FluentIcon icon="error-circle" size="11" />
                  {{ errors.trigger_conditions }}
                </p>

                <p class="text-xs text-n-slate-10 flex items-center gap-1">
                  <FluentIcon icon="info" size="11" />
                  {{ t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_STAGE_HINT') }}
                </p>
              </template>

              <!-- time_based -->
              <template v-else-if="form.trigger_type === 'time_based'">
                <!-- Mode selector -->
                <div class="flex flex-wrap gap-3">
                  <label
                    v-for="mode in ['every_n_days', 'cron_expression']"
                    :key="mode"
                    class="flex min-h-11 cursor-pointer items-center gap-2"
                  >
                    <input
                      v-model="timeBasedMode"
                      type="radio"
                      name="sequence-trigger-time-mode"
                      :value="mode"
                      class="accent-n-brand"
                    />
                    <span class="text-sm text-n-slate-12">
                      {{
                        t(
                          `PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_TIME_MODE_${mode === 'every_n_days' ? 'EVERY' : 'CRON'}`
                        )
                      }}
                    </span>
                  </label>
                </div>

                <!-- Every N days -->
                <div v-if="timeBasedMode === 'every_n_days'">
                  <label
                    for="sequence-trigger-frequency-days"
                    class="block text-xs font-medium text-n-slate-11 mb-1"
                  >
                    {{
                      t(
                        'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_TIME_EVERY_N_DAYS'
                      )
                    }}
                    <span class="text-ruby-11">*</span>
                  </label>
                  <div class="relative w-40">
                    <input
                      id="sequence-trigger-frequency-days"
                      v-model.number="form.trigger_conditions.every_n_days"
                      name="sequence-trigger-frequency-days"
                      type="number"
                      min="1"
                      max="365"
                      :aria-describedby="
                        errors.trigger_conditions
                          ? 'sequence-trigger-time-every-hint sequence-trigger-time-error'
                          : 'sequence-trigger-time-every-hint'
                      "
                      :aria-invalid="Boolean(errors.trigger_conditions)"
                      class="min-h-11 w-full pl-3 pr-10 py-2 rounded-md border bg-n-solid-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors"
                      :class="
                        errors.trigger_conditions
                          ? 'border-ruby-8'
                          : 'border-n-weak'
                      "
                    />
                    <span
                      class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-n-slate-10 pointer-events-none"
                    >
                      {{ t('PIPELINE.SEQUENCE_DEFINITIONS.SUFFIX_DAYS') }}
                    </span>
                  </div>
                  <p
                    id="sequence-trigger-time-every-hint"
                    class="text-xs text-n-slate-10 mt-1"
                  >
                    {{
                      t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_TIME_EVERY_HINT')
                    }}
                  </p>
                </div>

                <!-- Cron expression -->
                <div v-else>
                  <label
                    for="sequence-trigger-cron-expression"
                    class="block text-xs font-medium text-n-slate-11 mb-1"
                  >
                    {{
                      t(
                        'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_TIME_CRON_EXPRESSION'
                      )
                    }}
                    <span class="text-ruby-11">*</span>
                  </label>
                  <input
                    id="sequence-trigger-cron-expression"
                    v-model="form.trigger_conditions.cron_expression"
                    name="sequence-trigger-cron-expression"
                    type="text"
                    maxlength="255"
                    autocomplete="off"
                    spellcheck="false"
                    :aria-describedby="
                      errors.trigger_conditions
                        ? 'sequence-trigger-time-cron-hint sequence-trigger-time-error'
                        : 'sequence-trigger-time-cron-hint'
                    "
                    :aria-invalid="Boolean(errors.trigger_conditions)"
                    class="min-h-11 w-full px-3 py-2 rounded-md border bg-n-solid-2 text-sm text-n-slate-12 font-mono placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors"
                    :class="
                      errors.trigger_conditions
                        ? 'border-ruby-8'
                        : 'border-n-weak'
                    "
                    :placeholder="
                      t(
                        'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_TIME_CRON_PLACEHOLDER'
                      )
                    "
                  />
                  <p
                    id="sequence-trigger-time-cron-hint"
                    class="text-xs text-n-slate-10 mt-1"
                  >
                    {{
                      t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_TIME_CRON_HINT')
                    }}
                  </p>
                </div>

                <p
                  v-if="errors.trigger_conditions"
                  id="sequence-trigger-time-error"
                  role="alert"
                  class="text-xs text-ruby-11 flex items-center gap-1"
                >
                  <FluentIcon icon="error-circle" size="11" />
                  {{ errors.trigger_conditions }}
                </p>

                <!-- Advanced eligibility filters (collapsible) -->
                <div class="border-t border-n-weak pt-3">
                  <button
                    type="button"
                    class="flex min-h-11 items-center gap-1.5 rounded-lg text-xs font-medium text-n-slate-11 transition-colors hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                    :aria-expanded="showTimeFilters"
                    @click="
                      showTimeFilters = !showTimeFilters;
                      if (showTimeFilters) ensureEligibilityFilter();
                    "
                  >
                    <FluentIcon
                      :icon="showTimeFilters ? 'chevron-down' : 'chevron-right'"
                      size="11"
                    />
                    {{
                      t(
                        'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_TIME_FILTER_TITLE'
                      )
                    }}
                  </button>

                  <div
                    v-if="showTimeFilters"
                    class="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3"
                  >
                    <!-- Eligibility stage -->
                    <div>
                      <label
                        for="sequence-trigger-eligibility-stage"
                        class="block text-xs font-medium text-n-slate-11 mb-1"
                      >
                        {{
                          t(
                            'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_TIME_FILTER_STAGE'
                          )
                        }}
                      </label>
                      <select
                        id="sequence-trigger-eligibility-stage"
                        v-model="
                          form.trigger_conditions.eligibility_filter.stage_id
                        "
                        name="sequence-trigger-eligibility-stage"
                        class="!mb-0 min-h-11 w-full rounded-md border border-n-weak bg-n-solid-2 py-2 pl-3 pr-10 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                      >
                        <option value="">
                          {{
                            t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_STAGE_ANY')
                          }}
                        </option>
                        <option
                          v-for="stage in eligibilityStages"
                          :key="stage.id"
                          :value="stage.id"
                        >
                          {{ stage.name }}
                        </option>
                      </select>
                    </div>

                    <!-- Days in stage -->
                    <div>
                      <label
                        for="sequence-trigger-days-in-stage"
                        class="block text-xs font-medium text-n-slate-11 mb-1"
                      >
                        {{
                          t(
                            'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_TIME_FILTER_DAYS_IN_STAGE'
                          )
                        }}
                      </label>
                      <input
                        id="sequence-trigger-days-in-stage"
                        v-model.number="
                          form.trigger_conditions.eligibility_filter
                            .days_in_stage
                        "
                        name="sequence-trigger-days-in-stage"
                        type="number"
                        min="0"
                        :max="MAX_DAYS_IN_STAGE"
                        :aria-invalid="Boolean(errors.trigger_days_in_stage)"
                        :aria-describedby="
                          errors.trigger_days_in_stage
                            ? 'sequence-trigger-days-in-stage-error'
                            : undefined
                        "
                        class="min-h-11 w-full px-3 py-2 rounded-md border bg-n-solid-2 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors"
                        :class="
                          errors.trigger_days_in_stage
                            ? 'border-ruby-8'
                            : 'border-n-weak'
                        "
                      />
                      <p
                        v-if="errors.trigger_days_in_stage"
                        id="sequence-trigger-days-in-stage-error"
                        role="alert"
                        class="mt-1 flex items-center gap-1 text-xs text-ruby-11"
                      >
                        <FluentIcon icon="error-circle" size="11" />
                        {{ errors.trigger_days_in_stage }}
                      </p>
                    </div>
                  </div>
                </div>

                <p class="text-xs text-n-slate-10 flex items-center gap-1">
                  <FluentIcon icon="info" size="11" />
                  {{ t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_TIME_HINT') }}
                </p>
              </template>

              <!-- condition_based -->
              <template v-else-if="form.trigger_type === 'condition_based'">
                <!-- Conjunction toggle -->
                <div class="flex flex-wrap gap-3">
                  <label
                    v-for="conj in ['and', 'or']"
                    :key="conj"
                    class="flex min-h-11 cursor-pointer items-center gap-2"
                  >
                    <input
                      v-model="form.trigger_conditions.conjunction"
                      type="radio"
                      :value="conj"
                      class="accent-n-brand"
                    />
                    <span class="text-sm text-n-slate-12">
                      {{
                        t(
                          `PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_CONDITION_CONJUNCTION_${conj.toUpperCase()}`
                        )
                      }}
                    </span>
                  </label>
                </div>

                <!-- Single condition row -->
                <div class="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  <!-- Field selector -->
                  <div>
                    <label
                      for="sequence-trigger-condition-field"
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{
                        t(
                          'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_CONDITION_FIELD'
                        )
                      }}
                      <span class="text-ruby-11">*</span>
                    </label>
                    <select
                      id="sequence-trigger-condition-field"
                      v-model="form.trigger_conditions.field"
                      name="sequence-trigger-condition-field"
                      class="!mb-0 min-h-11 w-full rounded-md border bg-n-solid-2 py-2 pl-3 pr-10 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                      :class="
                        errors.trigger_conditions
                          ? 'border-ruby-8'
                          : 'border-n-weak'
                      "
                      @change="
                        form.trigger_conditions.operator = '';
                        form.trigger_conditions.value = '';
                      "
                    >
                      <option value="">—</option>
                      <option
                        v-for="cf in CONDITION_FIELDS"
                        :key="cf.value"
                        :value="cf.value"
                      >
                        {{ t(`PIPELINE.SEQUENCE_DEFINITIONS.${cf.labelKey}`) }}
                      </option>
                    </select>
                  </div>

                  <!-- Operator selector -->
                  <div>
                    <label
                      for="sequence-trigger-condition-operator"
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{
                        t(
                          'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_CONDITION_OPERATOR'
                        )
                      }}
                      <span class="text-ruby-11">*</span>
                    </label>
                    <select
                      id="sequence-trigger-condition-operator"
                      v-model="form.trigger_conditions.operator"
                      name="sequence-trigger-condition-operator"
                      class="!mb-0 min-h-11 w-full rounded-md border bg-n-solid-2 py-2 pl-3 pr-10 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                      :class="
                        errors.trigger_conditions
                          ? 'border-ruby-8'
                          : 'border-n-weak'
                      "
                    >
                      <option value="">—</option>
                      <option
                        v-for="op in operatorsForField(
                          form.trigger_conditions.field
                        )"
                        :key="op.value"
                        :value="op.value"
                      >
                        {{ t(`PIPELINE.SEQUENCE_DEFINITIONS.${op.labelKey}`) }}
                      </option>
                    </select>
                  </div>

                  <!-- Value input (contextual) -->
                  <div>
                    <label
                      for="sequence-trigger-condition-value"
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{
                        t(
                          'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_CONDITION_VALUE'
                        )
                      }}
                      <span class="text-ruby-11">*</span>
                    </label>

                    <!-- Stage picker -->
                    <select
                      v-if="
                        CONDITION_FIELDS.find(
                          f => f.value === form.trigger_conditions.field
                        )?.type === 'stage'
                      "
                      id="sequence-trigger-condition-value"
                      v-model="form.trigger_conditions.value"
                      name="sequence-trigger-condition-value"
                      class="!mb-0 min-h-11 w-full rounded-md border bg-n-solid-2 py-2 pl-3 pr-10 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                      :class="
                        errors.trigger_conditions
                          ? 'border-ruby-8'
                          : 'border-n-weak'
                      "
                    >
                      <option value="">—</option>
                      <option
                        v-for="stage in allStages"
                        :key="stage.id"
                        :value="stage.id"
                      >
                        {{ stage.name }}
                      </option>
                    </select>

                    <!-- Priority picker -->
                    <select
                      v-else-if="
                        CONDITION_FIELDS.find(
                          f => f.value === form.trigger_conditions.field
                        )?.type === 'priority'
                      "
                      id="sequence-trigger-condition-value"
                      v-model="form.trigger_conditions.value"
                      name="sequence-trigger-condition-value"
                      class="!mb-0 min-h-11 w-full rounded-md border bg-n-solid-2 py-2 pl-3 pr-10 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                      :class="
                        errors.trigger_conditions
                          ? 'border-ruby-8'
                          : 'border-n-weak'
                      "
                    >
                      <option value="">—</option>
                      <option v-for="p in PRIORITY_OPTIONS" :key="p" :value="p">
                        {{ p }}
                      </option>
                    </select>

                    <!-- Numeric / text input -->
                    <input
                      v-else
                      id="sequence-trigger-condition-value"
                      v-model="form.trigger_conditions.value"
                      name="sequence-trigger-condition-value"
                      type="number"
                      class="min-h-11 w-full px-3 py-2 rounded-md border bg-n-solid-2 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors"
                      :class="
                        errors.trigger_conditions
                          ? 'border-ruby-8'
                          : 'border-n-weak'
                      "
                    />
                  </div>
                </div>

                <!-- "Add condition" disabled button -->
                <button
                  type="button"
                  disabled
                  class="flex min-h-11 items-center gap-1.5 px-3 py-1.5 rounded-lg border border-dashed border-n-weak text-xs font-medium text-n-slate-10 cursor-not-allowed"
                  :title="
                    t(
                      'PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_CONDITION_ADD_ROW_DISABLED'
                    )
                  "
                >
                  <FluentIcon icon="add" size="11" />
                  {{
                    t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_CONDITION_ADD_ROW')
                  }}
                </button>

                <p
                  v-if="errors.trigger_conditions"
                  class="text-xs text-ruby-11 flex items-center gap-1"
                >
                  <FluentIcon icon="error-circle" size="11" />
                  {{ errors.trigger_conditions }}
                </p>

                <p class="text-xs text-n-slate-10 flex items-center gap-1">
                  <FluentIcon icon="info" size="11" />
                  {{
                    t('PIPELINE.SEQUENCE_DEFINITIONS.TRIGGER_CONDITION_HINT')
                  }}
                </p>
              </template>
            </div>
            <!-- ── End trigger configuration ─────────────────────────────── -->

            <div
              class="flex items-center justify-between pt-2 border-t border-n-weak"
            >
              <div class="min-w-0 pr-4">
                <p class="text-sm font-medium text-n-slate-12">
                  {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_ACTIVE_LABEL') }}
                </p>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_ACTIVE_HINT') }}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                :aria-checked="form.active"
                class="relative inline-flex h-7 min-h-11 w-12 flex-shrink-0 items-center rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                :aria-label="
                  t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_ACTIVE_LABEL')
                "
                :class="form.active ? 'bg-grass-9' : 'bg-n-alpha-3'"
                @click="form.active = !form.active"
              >
                <span
                  class="inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform"
                  :class="form.active ? 'translate-x-5' : 'translate-x-0.5'"
                />
              </button>
            </div>
          </div>
        </section>

        <!-- Steps -->
        <section
          class="bg-n-solid-1 border border-n-weak rounded-2xl overflow-hidden"
        >
          <div
            class="px-6 py-4 border-b border-n-weak bg-n-alpha-1 flex items-center justify-between gap-4"
          >
            <div class="flex items-center gap-3">
              <div
                class="flex items-center justify-center w-9 h-9 rounded-lg bg-iris-3 text-iris-11"
              >
                <FluentIcon icon="flash-on" size="16" />
              </div>
              <div>
                <h2 class="text-base font-semibold text-n-slate-12">
                  {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEPS_TITLE') }}
                </h2>
                <p class="text-xs text-n-slate-10 mt-0.5">
                  {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEPS_HINT') }}
                </p>
              </div>
            </div>
            <div class="flex items-center gap-2">
              <span
                v-if="form.steps.length"
                class="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-n-alpha-2 text-xs font-medium text-n-slate-11"
              >
                <FluentIcon icon="calendar-clock" size="11" />
                {{ totalDurationLabel }}
              </span>
              <button
                type="button"
                class="inline-flex min-h-11 items-center gap-1.5 rounded-lg bg-n-brand px-3 py-1.5 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-n-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                @click="addStep"
              >
                <FluentIcon icon="add" size="12" />
                {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_ADD') }}
              </button>
            </div>
          </div>

          <div class="p-6 space-y-3">
            <p
              v-if="errors.steps"
              class="text-xs text-ruby-11 flex items-center gap-1 px-3 py-2 bg-ruby-3 rounded-md"
            >
              <FluentIcon icon="error-circle" size="12" />
              {{ errors.steps }}
            </p>

            <div
              v-for="(step, idx) in form.steps"
              :key="idx"
              class="border border-n-weak rounded-xl bg-n-solid-2 overflow-hidden"
            >
              <!-- Step header -->
              <div
                class="flex items-center gap-3 px-4 py-3 border-b border-n-weak bg-n-alpha-1"
              >
                <div
                  class="flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0"
                  :class="
                    ACTIVITY_META[step.activity_type]?.tone ||
                    'bg-n-alpha-2 text-n-slate-11'
                  "
                >
                  <FluentIcon
                    :icon="
                      ACTIVITY_META[step.activity_type]?.icon || 'add-circle'
                    "
                    size="16"
                  />
                </div>
                <div class="flex-1 min-w-0">
                  <span class="text-sm font-semibold text-n-slate-12">
                    {{
                      t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_NUMBER', {
                        n: idx + 1,
                      })
                    }}
                  </span>
                  <span
                    v-if="Number(step.delay_days) || Number(step.delay_hours)"
                    class="ml-2 text-xs text-n-slate-10"
                  >
                    {{
                      t('PIPELINE.SEQUENCE_DEFINITIONS.STEP_DELAY_INLINE', {
                        d: step.delay_days || 0,
                        h: step.delay_hours || 0,
                      })
                    }}
                  </span>
                </div>
                <div class="inline-flex items-center gap-0.5">
                  <button
                    type="button"
                    :disabled="idx === 0"
                    class="min-h-11 min-w-11 rounded-md p-2 text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-30"
                    :aria-label="
                      t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_MOVE_UP')
                    "
                    @click="moveStep(idx, -1)"
                  >
                    <FluentIcon icon="arrow-up" size="13" />
                  </button>
                  <button
                    type="button"
                    :disabled="idx === form.steps.length - 1"
                    class="min-h-11 min-w-11 rounded-md p-2 text-n-slate-10 transition-colors hover:bg-n-alpha-2 hover:text-n-slate-12 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-30"
                    :aria-label="
                      t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_MOVE_DOWN')
                    "
                    @click="moveStep(idx, 1)"
                  >
                    <FluentIcon icon="arrow-download" size="13" />
                  </button>
                  <button
                    type="button"
                    class="min-h-11 min-w-11 rounded-md p-2 text-ruby-10 transition-colors hover:bg-ruby-3 hover:text-ruby-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                    :aria-label="
                      t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_REMOVE')
                    "
                    @click="removeStep(idx)"
                  >
                    <FluentIcon icon="delete" size="13" />
                  </button>
                </div>
              </div>

              <!-- Step body -->
              <div class="p-4 space-y-4">
                <!-- Type selector + title row -->
                <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <!-- Categorized type picker -->
                  <div>
                    <label
                      :id="`sequence-step-${idx}-type-label`"
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{
                        t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_TYPE_LABEL')
                      }}
                      <span class="text-ruby-11">*</span>
                    </label>
                    <!-- Current type badge + open accordion button -->
                    <button
                      type="button"
                      data-testid="sequence-step-type-picker"
                      class="flex min-h-11 w-full items-center gap-2 rounded-md border border-n-weak bg-n-solid-3 px-3 py-2 text-left transition-colors hover:border-n-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                      :aria-expanded="Boolean(openCategoryForStep(idx))"
                      :aria-labelledby="`sequence-step-${idx}-type-label`"
                      @click="
                        toggleCategory(
                          idx,
                          ACTIVITY_META[step.activity_type]?.category ||
                            'activity'
                        )
                      "
                    >
                      <span
                        class="flex items-center justify-center w-6 h-6 rounded flex-shrink-0"
                        :class="
                          ACTIVITY_META[step.activity_type]?.tone ||
                          'bg-n-alpha-2 text-n-slate-11'
                        "
                      >
                        <FluentIcon
                          :icon="
                            ACTIVITY_META[step.activity_type]?.icon ||
                            'add-circle'
                          "
                          size="12"
                        />
                      </span>
                      <span class="flex-1 text-sm text-n-slate-12 truncate">
                        {{
                          t(
                            `PIPELINE.SEQUENCE_DEFINITIONS.ACTIVITY_TYPE_${step.activity_type.toUpperCase()}`
                          )
                        }}
                      </span>
                      <FluentIcon
                        :icon="
                          openCategoryForStep(idx)
                            ? 'chevron-down'
                            : 'chevron-down'
                        "
                        size="12"
                        class="text-n-slate-10 flex-shrink-0"
                      />
                    </button>

                    <!-- Category accordion -->
                    <div
                      v-if="openCategoryForStep(idx)"
                      class="mt-1 rounded-lg border border-n-weak bg-n-solid-3 overflow-hidden"
                    >
                      <div
                        v-for="cat in availableCategories"
                        :key="cat.key"
                        class="border-b border-n-weak last:border-b-0"
                      >
                        <!-- Category header -->
                        <button
                          type="button"
                          class="flex min-h-11 w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-n-alpha-1 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-n-brand"
                          :aria-expanded="openCategoryForStep(idx) === cat.key"
                          @click="toggleCategory(idx, cat.key)"
                        >
                          <span
                            class="flex items-center justify-center w-6 h-6 rounded flex-shrink-0"
                            :class="cat.tone"
                          >
                            <FluentIcon :icon="cat.icon" size="11" />
                          </span>
                          <span
                            class="flex-1 text-xs font-semibold text-n-slate-11 uppercase tracking-wide"
                          >
                            {{
                              t(
                                `PIPELINE.SEQUENCE_DEFINITIONS.CATEGORY_${cat.key.toUpperCase()}`
                              )
                            }}
                          </span>
                          <FluentIcon
                            :icon="
                              openCategoryForStep(idx) === cat.key
                                ? 'chevron-down'
                                : 'chevron-right'
                            "
                            size="11"
                            class="text-n-slate-10"
                          />
                        </button>

                        <!-- Types in this category -->
                        <div
                          v-if="openCategoryForStep(idx) === cat.key"
                          class="bg-n-alpha-1 px-2 pb-2 space-y-0.5"
                        >
                          <button
                            v-for="type in typesByCategory[cat.key]"
                            :key="type"
                            type="button"
                            class="flex min-h-11 w-full items-center gap-2 rounded-md px-2 py-1.5 text-left transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                            :class="
                              step.activity_type === type
                                ? 'bg-n-brand/10 text-n-slate-12'
                                : 'hover:bg-n-alpha-2 text-n-slate-11'
                            "
                            @click="selectType(idx, type)"
                          >
                            <span
                              class="flex items-center justify-center w-5 h-5 rounded flex-shrink-0"
                              :class="
                                ACTIVITY_META[type]?.tone ||
                                'bg-n-alpha-2 text-n-slate-11'
                              "
                            >
                              <FluentIcon
                                :icon="
                                  ACTIVITY_META[type]?.icon || 'add-circle'
                                "
                                size="11"
                              />
                            </span>
                            <span class="text-xs font-medium">
                              {{
                                t(
                                  `PIPELINE.SEQUENCE_DEFINITIONS.ACTIVITY_TYPE_${type.toUpperCase()}`
                                )
                              }}
                            </span>
                            <FluentIcon
                              v-if="step.activity_type === type"
                              icon="checkmark-circle"
                              size="11"
                              class="ml-auto text-n-brand"
                            />
                          </button>
                        </div>
                      </div>
                    </div>

                    <p
                      v-if="errors[`step_${idx}_type`]"
                      class="text-xs text-ruby-11 mt-1"
                    >
                      {{ errors[`step_${idx}_type`] }}
                    </p>
                  </div>

                  <!-- Title field -->
                  <div>
                    <label
                      :for="`sequence-step-${idx}-title`"
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{
                        t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_TITLE_LABEL')
                      }}
                      <span class="text-ruby-11">*</span>
                    </label>
                    <input
                      :id="`sequence-step-${idx}-title`"
                      v-model="step.title"
                      :name="`sequence-step-${idx}-title`"
                      type="text"
                      autocomplete="off"
                      class="min-h-11 w-full px-3 py-2 rounded-md border border-n-weak bg-n-solid-3 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors"
                      :placeholder="
                        t(
                          'PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_TITLE_PLACEHOLDER'
                        )
                      "
                    />
                    <p
                      v-if="errors[`step_${idx}_title`]"
                      class="text-xs text-ruby-11 mt-1"
                    >
                      {{ errors[`step_${idx}_title`] }}
                    </p>
                  </div>
                </div>

                <!-- Description -->
                <div>
                  <label
                    :for="`sequence-step-${idx}-description`"
                    class="block text-xs font-medium text-n-slate-11 mb-1"
                  >
                    {{
                      t(
                        'PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_DESCRIPTION_LABEL'
                      )
                    }}
                  </label>
                  <input
                    :id="`sequence-step-${idx}-description`"
                    v-model="step.description"
                    :name="`sequence-step-${idx}-description`"
                    type="text"
                    autocomplete="off"
                    class="min-h-11 w-full px-3 py-2 rounded-md border border-n-weak bg-n-solid-3 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors"
                  />
                </div>

                <!-- Delay row (always visible except for delay type where it's the main content) -->
                <div
                  v-if="step.activity_type === 'delay'"
                  class="rounded-lg bg-n-alpha-1 border border-n-weak p-3 space-y-2"
                >
                  <p class="text-xs text-n-slate-10 flex items-center gap-1">
                    <FluentIcon icon="info" size="12" />
                    {{ t('PIPELINE.SEQUENCE_DEFINITIONS.DELAY_HINT') }}
                  </p>
                  <div class="grid grid-cols-2 gap-3">
                    <div>
                      <label
                        :for="`sequence-step-${idx}-delay-days`"
                        class="block text-xs font-medium text-n-slate-11 mb-1"
                      >
                        {{
                          t(
                            'PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_DELAY_DAYS_LABEL'
                          )
                        }}
                      </label>
                      <div class="relative">
                        <input
                          :id="`sequence-step-${idx}-delay-days`"
                          v-model.number="step.delay_days"
                          :name="`sequence-step-${idx}-delay-days`"
                          type="number"
                          min="0"
                          max="3650"
                          :aria-invalid="
                            Boolean(errors[`step_${idx}_delay_days`])
                          "
                          :aria-describedby="
                            errors[`step_${idx}_delay_days`]
                              ? `sequence-step-${idx}-delay-days-error`
                              : undefined
                          "
                          class="min-h-11 w-full pl-3 pr-10 py-2 rounded-md border bg-n-solid-3 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors"
                          :class="
                            errors[`step_${idx}_delay_days`]
                              ? 'border-ruby-8'
                              : 'border-n-weak'
                          "
                        />
                        <span
                          class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-n-slate-10 pointer-events-none"
                        >
                          {{ t('PIPELINE.SEQUENCE_DEFINITIONS.SUFFIX_DAYS') }}
                        </span>
                      </div>
                      <p
                        v-if="errors[`step_${idx}_delay_days`]"
                        :id="`sequence-step-${idx}-delay-days-error`"
                        class="mt-1 text-xs text-ruby-11"
                      >
                        {{ errors[`step_${idx}_delay_days`] }}
                      </p>
                    </div>
                    <div>
                      <label
                        :for="`sequence-step-${idx}-delay-hours`"
                        class="block text-xs font-medium text-n-slate-11 mb-1"
                      >
                        {{
                          t(
                            'PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_DELAY_HOURS_LABEL'
                          )
                        }}
                      </label>
                      <div class="relative">
                        <input
                          :id="`sequence-step-${idx}-delay-hours`"
                          v-model.number="step.delay_hours"
                          :name="`sequence-step-${idx}-delay-hours`"
                          type="number"
                          min="0"
                          max="23"
                          :aria-invalid="
                            Boolean(errors[`step_${idx}_delay_hours`])
                          "
                          :aria-describedby="
                            errors[`step_${idx}_delay_hours`]
                              ? `sequence-step-${idx}-delay-hours-error`
                              : undefined
                          "
                          class="min-h-11 w-full pl-3 pr-10 py-2 rounded-md border bg-n-solid-3 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors"
                          :class="
                            errors[`step_${idx}_delay_hours`]
                              ? 'border-ruby-8'
                              : 'border-n-weak'
                          "
                        />
                        <span
                          class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-n-slate-10 pointer-events-none"
                        >
                          {{ t('PIPELINE.SEQUENCE_DEFINITIONS.SUFFIX_HOURS') }}
                        </span>
                      </div>
                      <p
                        v-if="errors[`step_${idx}_delay_hours`]"
                        :id="`sequence-step-${idx}-delay-hours-error`"
                        class="mt-1 text-xs text-ruby-11"
                      >
                        {{ errors[`step_${idx}_delay_hours`] }}
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Delay fields for non-delay types -->
                <div v-else class="grid grid-cols-2 gap-3">
                  <div>
                    <label
                      :for="`sequence-step-${idx}-delay-days`"
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{
                        t(
                          'PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_DELAY_DAYS_LABEL'
                        )
                      }}
                    </label>
                    <div class="relative">
                      <input
                        :id="`sequence-step-${idx}-delay-days`"
                        v-model.number="step.delay_days"
                        :name="`sequence-step-${idx}-delay-days`"
                        type="number"
                        min="0"
                        max="3650"
                        :aria-invalid="
                          Boolean(errors[`step_${idx}_delay_days`])
                        "
                        :aria-describedby="
                          errors[`step_${idx}_delay_days`]
                            ? `sequence-step-${idx}-delay-days-error`
                            : undefined
                        "
                        class="min-h-11 w-full pl-3 pr-10 py-2 rounded-md border bg-n-solid-3 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors"
                        :class="
                          errors[`step_${idx}_delay_days`]
                            ? 'border-ruby-8'
                            : 'border-n-weak'
                        "
                      />
                      <span
                        class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-n-slate-10 pointer-events-none"
                      >
                        {{ t('PIPELINE.SEQUENCE_DEFINITIONS.SUFFIX_DAYS') }}
                      </span>
                    </div>
                    <p
                      v-if="errors[`step_${idx}_delay_days`]"
                      :id="`sequence-step-${idx}-delay-days-error`"
                      class="mt-1 text-xs text-ruby-11"
                    >
                      {{ errors[`step_${idx}_delay_days`] }}
                    </p>
                  </div>
                  <div>
                    <label
                      :for="`sequence-step-${idx}-delay-hours`"
                      class="block text-xs font-medium text-n-slate-11 mb-1"
                    >
                      {{
                        t(
                          'PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_DELAY_HOURS_LABEL'
                        )
                      }}
                    </label>
                    <div class="relative">
                      <input
                        :id="`sequence-step-${idx}-delay-hours`"
                        v-model.number="step.delay_hours"
                        :name="`sequence-step-${idx}-delay-hours`"
                        type="number"
                        min="0"
                        max="23"
                        :aria-invalid="
                          Boolean(errors[`step_${idx}_delay_hours`])
                        "
                        :aria-describedby="
                          errors[`step_${idx}_delay_hours`]
                            ? `sequence-step-${idx}-delay-hours-error`
                            : undefined
                        "
                        class="min-h-11 w-full pl-3 pr-10 py-2 rounded-md border bg-n-solid-3 text-sm text-n-slate-12 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors"
                        :class="
                          errors[`step_${idx}_delay_hours`]
                            ? 'border-ruby-8'
                            : 'border-n-weak'
                        "
                      />
                      <span
                        class="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-n-slate-10 pointer-events-none"
                      >
                        {{ t('PIPELINE.SEQUENCE_DEFINITIONS.SUFFIX_HOURS') }}
                      </span>
                    </div>
                    <p
                      v-if="errors[`step_${idx}_delay_hours`]"
                      :id="`sequence-step-${idx}-delay-hours-error`"
                      class="mt-1 text-xs text-ruby-11"
                    >
                      {{ errors[`step_${idx}_delay_hours`] }}
                    </p>
                  </div>
                </div>

                <div
                  v-if="
                    ACTIVITY_META[step.activity_type]?.category === 'activity'
                  "
                  class="grid grid-cols-1 gap-3 sm:grid-cols-3"
                >
                  <div>
                    <label
                      :for="`sequence-step-${idx}-duration`"
                      class="mb-1 block text-xs font-medium text-n-slate-11"
                    >
                      {{
                        t(
                          'PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_DURATION_MINUTES_LABEL'
                        )
                      }}
                    </label>
                    <input
                      :id="`sequence-step-${idx}-duration`"
                      v-model.number="step.duration"
                      :name="`sequence-step-${idx}-duration`"
                      type="number"
                      min="0"
                      max="10080"
                      :aria-invalid="Boolean(errors[`step_${idx}_duration`])"
                      :aria-describedby="
                        errors[`step_${idx}_duration`]
                          ? `sequence-step-${idx}-duration-error`
                          : undefined
                      "
                      class="min-h-11 w-full rounded-md border bg-n-solid-3 px-3 py-2 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                      :class="
                        errors[`step_${idx}_duration`]
                          ? 'border-ruby-8'
                          : 'border-n-weak'
                      "
                    />
                    <p
                      v-if="errors[`step_${idx}_duration`]"
                      :id="`sequence-step-${idx}-duration-error`"
                      class="mt-1 text-xs text-ruby-11"
                    >
                      {{ errors[`step_${idx}_duration`] }}
                    </p>
                  </div>
                  <div>
                    <label
                      :for="`sequence-step-${idx}-schedule-hours`"
                      class="mb-1 block text-xs font-medium text-n-slate-11"
                    >
                      {{
                        t(
                          'PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_SCHEDULE_HOURS_LABEL'
                        )
                      }}
                    </label>
                    <input
                      :id="`sequence-step-${idx}-schedule-hours`"
                      v-model.number="step.schedule_hours"
                      :name="`sequence-step-${idx}-schedule-hours`"
                      type="number"
                      min="0"
                      max="87600"
                      :aria-invalid="
                        Boolean(errors[`step_${idx}_schedule_hours`])
                      "
                      :aria-describedby="
                        errors[`step_${idx}_schedule_hours`]
                          ? `sequence-step-${idx}-schedule-hours-error`
                          : undefined
                      "
                      class="min-h-11 w-full rounded-md border bg-n-solid-3 px-3 py-2 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                      :class="
                        errors[`step_${idx}_schedule_hours`]
                          ? 'border-ruby-8'
                          : 'border-n-weak'
                      "
                    />
                    <p
                      v-if="errors[`step_${idx}_schedule_hours`]"
                      :id="`sequence-step-${idx}-schedule-hours-error`"
                      class="mt-1 text-xs text-ruby-11"
                    >
                      {{ errors[`step_${idx}_schedule_hours`] }}
                    </p>
                  </div>
                  <div>
                    <label
                      :for="`sequence-step-${idx}-due-days`"
                      class="mb-1 block text-xs font-medium text-n-slate-11"
                    >
                      {{
                        t(
                          'PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_DUE_DAYS_LABEL'
                        )
                      }}
                    </label>
                    <input
                      :id="`sequence-step-${idx}-due-days`"
                      v-model.number="step.due_days"
                      :name="`sequence-step-${idx}-due-days`"
                      type="number"
                      min="0"
                      max="3650"
                      :aria-invalid="Boolean(errors[`step_${idx}_due_days`])"
                      :aria-describedby="
                        errors[`step_${idx}_due_days`]
                          ? `sequence-step-${idx}-due-days-error`
                          : undefined
                      "
                      class="min-h-11 w-full rounded-md border bg-n-solid-3 px-3 py-2 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                      :class="
                        errors[`step_${idx}_due_days`]
                          ? 'border-ruby-8'
                          : 'border-n-weak'
                      "
                    />
                    <p
                      v-if="errors[`step_${idx}_due_days`]"
                      :id="`sequence-step-${idx}-due-days-error`"
                      class="mt-1 text-xs text-ruby-11"
                    >
                      {{ errors[`step_${idx}_due_days`] }}
                    </p>
                  </div>
                </div>

                <!-- ── Webhook fields ───────────────────────────────────── -->
                <template v-if="step.activity_type === 'webhook'">
                  <div class="space-y-3 pt-1 border-t border-n-weak">
                    <!-- URL + Method row -->
                    <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                      <div class="md:col-span-2">
                        <label
                          :for="`sequence-step-${idx}-webhook-url`"
                          class="block text-xs font-medium text-n-slate-11 mb-1"
                        >
                          {{
                            t('PIPELINE.SEQUENCE_DEFINITIONS.FIELD_WEBHOOK_URL')
                          }}
                          <span class="text-ruby-11">*</span>
                        </label>
                        <input
                          :id="`sequence-step-${idx}-webhook-url`"
                          v-model="step.webhook_url"
                          :name="`sequence-step-${idx}-webhook-url`"
                          type="url"
                          class="min-h-11 w-full px-3 py-2 rounded-md border bg-n-solid-3 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors"
                          :class="
                            errors[`step_${idx}_webhook_url`]
                              ? 'border-ruby-8'
                              : 'border-n-weak'
                          "
                          :placeholder="
                            t(
                              'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_WEBHOOK_URL_PLACEHOLDER'
                            )
                          "
                        />
                        <p
                          v-if="errors[`step_${idx}_webhook_url`]"
                          class="text-xs text-ruby-11 mt-1 flex items-center gap-1"
                        >
                          <FluentIcon icon="error-circle" size="11" />
                          {{ errors[`step_${idx}_webhook_url`] }}
                        </p>
                      </div>
                      <div>
                        <label
                          :for="`sequence-step-${idx}-webhook-method`"
                          class="block text-xs font-medium text-n-slate-11 mb-1"
                        >
                          {{
                            t(
                              'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_WEBHOOK_METHOD'
                            )
                          }}
                        </label>
                        <select
                          :id="`sequence-step-${idx}-webhook-method`"
                          v-model="step.webhook_method"
                          :name="`sequence-step-${idx}-webhook-method`"
                          class="!mb-0 min-h-11 w-full rounded-md border border-n-weak bg-n-solid-3 py-2 pl-3 pr-10 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                        >
                          <option value="POST">{{ 'POST' }}</option>
                          <option value="GET">{{ 'GET' }}</option>
                          <option value="PUT">{{ 'PUT' }}</option>
                        </select>
                      </div>
                    </div>

                    <!-- Custom headers -->
                    <div>
                      <div class="flex items-center justify-between mb-1">
                        <label
                          class="block text-xs font-medium text-n-slate-11"
                        >
                          {{
                            t(
                              'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_WEBHOOK_HEADERS'
                            )
                          }}
                        </label>
                        <button
                          v-if="(step.webhook_headers || []).length < 5"
                          type="button"
                          class="min-h-11 rounded-md px-2 text-xs font-medium text-n-brand transition-colors hover:text-n-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                          @click="addHeader(step)"
                        >
                          {{
                            t(
                              'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_WEBHOOK_HEADER_ADD'
                            )
                          }}
                        </button>
                      </div>
                      <div
                        v-if="(step.webhook_headers || []).length"
                        class="space-y-1.5"
                      >
                        <div
                          v-for="(header, hIdx) in step.webhook_headers"
                          :key="hIdx"
                          class="flex items-center gap-2"
                        >
                          <input
                            v-model="header.key"
                            type="text"
                            :aria-label="
                              t(
                                'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_WEBHOOK_HEADER_KEY'
                              )
                            "
                            class="min-h-11 flex-1 px-3 py-1.5 rounded-md border border-n-weak bg-n-solid-3 text-xs text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/20"
                            :placeholder="
                              t(
                                'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_WEBHOOK_HEADER_KEY'
                              )
                            "
                          />
                          <input
                            v-model="header.value"
                            type="password"
                            autocomplete="new-password"
                            :aria-label="
                              t(
                                'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_WEBHOOK_HEADER_VALUE'
                              )
                            "
                            class="min-h-11 flex-1 px-3 py-1.5 rounded-md border border-n-weak bg-n-solid-3 text-xs text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand focus:ring-1 focus:ring-n-brand/20"
                            :placeholder="
                              header.configured
                                ? t(
                                    'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_WEBHOOK_HEADER_VALUE_CONFIGURED'
                                  )
                                : t(
                                    'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_WEBHOOK_HEADER_VALUE'
                                  )
                            "
                          />
                          <button
                            type="button"
                            class="min-h-11 min-w-11 flex-shrink-0 rounded-md p-2 text-ruby-10 transition-colors hover:bg-ruby-3 hover:text-ruby-11 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                            :aria-label="t('PIPELINE.DELETE')"
                            @click="removeHeader(step, hIdx)"
                          >
                            <FluentIcon icon="dismiss" size="12" />
                          </button>
                        </div>
                      </div>
                      <button
                        v-else
                        type="button"
                        class="min-h-11 rounded-md px-2 text-xs font-medium text-n-brand transition-colors hover:text-n-brand-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                        @click="addHeader(step)"
                      >
                        {{
                          t(
                            'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_WEBHOOK_HEADER_ADD'
                          )
                        }}
                      </button>
                    </div>

                    <!-- Payload template -->
                    <div>
                      <label
                        :for="`sequence-step-${idx}-webhook-payload`"
                        class="block text-xs font-medium text-n-slate-11 mb-1"
                      >
                        {{
                          t(
                            'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_WEBHOOK_PAYLOAD'
                          )
                        }}
                      </label>
                      <textarea
                        :id="`sequence-step-${idx}-webhook-payload`"
                        v-model="step.webhook_payload_template"
                        :name="`sequence-step-${idx}-webhook-payload`"
                        rows="4"
                        class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-solid-3 text-xs text-n-slate-12 font-mono placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors resize-y"
                        placeholder='{"event": "{{contact.name}}"}'
                      />
                    </div>

                    <!-- On failure -->
                    <div>
                      <label
                        class="block text-xs font-medium text-n-slate-11 mb-2"
                      >
                        {{
                          t('PIPELINE.SEQUENCE_DEFINITIONS.FIELD_ON_FAILURE')
                        }}
                      </label>
                      <div class="flex flex-wrap gap-2">
                        <label
                          v-for="opt in ['continue', 'pause', 'cancel']"
                          :key="opt"
                          class="flex min-h-11 cursor-pointer items-center gap-2"
                        >
                          <input
                            v-model="step.on_failure"
                            type="radio"
                            :name="`step_${idx}_on_failure`"
                            :value="opt"
                            class="accent-n-brand"
                          />
                          <span class="text-xs text-n-slate-12">
                            {{
                              t(
                                `PIPELINE.SEQUENCE_DEFINITIONS.OPTION_${opt.toUpperCase()}`
                              )
                            }}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- ── WhatsApp Message fields ──────────────────────────── -->
                <template v-else-if="step.activity_type === 'whatsapp_message'">
                  <div class="space-y-3 pt-1 border-t border-n-weak">
                    <!-- Inbox selector -->
                    <div>
                      <label
                        :for="`sequence-step-${idx}-inbox`"
                        class="block text-xs font-medium text-n-slate-11 mb-1"
                      >
                        {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FIELD_INBOX') }}
                      </label>
                      <select
                        :id="`sequence-step-${idx}-inbox`"
                        v-model="step.inbox_id"
                        :name="`sequence-step-${idx}-inbox`"
                        class="!mb-0 min-h-11 w-full rounded-md border border-n-weak bg-n-solid-3 py-2 pl-3 pr-10 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                      >
                        <option value="">
                          {{
                            t(
                              'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_INBOX_PLACEHOLDER'
                            )
                          }}
                        </option>
                        <option
                          v-for="inbox in whatsappInboxes"
                          :key="inbox.id"
                          :value="inbox.id"
                        >
                          {{ inbox.name }}
                        </option>
                      </select>
                    </div>

                    <!-- Message template textarea -->
                    <div>
                      <label
                        :for="`sequence-step-${idx}-message-template`"
                        class="block text-xs font-medium text-n-slate-11 mb-1"
                      >
                        {{
                          t(
                            'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_MESSAGE_TEMPLATE'
                          )
                        }}
                        <span class="text-ruby-11">*</span>
                      </label>
                      <textarea
                        :id="`sequence-step-${idx}-message-template`"
                        :ref="el => setTextareaRef(idx, 'message_template', el)"
                        v-model="step.message_template"
                        :name="`sequence-step-${idx}-message-template`"
                        rows="4"
                        class="w-full px-3 py-2 rounded-md border bg-n-solid-3 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors resize-y"
                        :class="
                          errors[`step_${idx}_message_template`]
                            ? 'border-ruby-8'
                            : 'border-n-weak'
                        "
                        :placeholder="
                          t(
                            'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_MESSAGE_TEMPLATE_PLACEHOLDER'
                          )
                        "
                      />
                      <p
                        v-if="errors[`step_${idx}_message_template`]"
                        class="text-xs text-ruby-11 mt-1 flex items-center gap-1"
                      >
                        <FluentIcon icon="error-circle" size="11" />
                        {{ errors[`step_${idx}_message_template`] }}
                      </p>

                      <!-- Liquid variable chips -->
                      <div class="mt-2">
                        <p class="text-xs text-n-slate-10 mb-1.5">
                          {{
                            t(
                              'PIPELINE.SEQUENCE_DEFINITIONS.VARIABLES_AVAILABLE'
                            )
                          }}
                        </p>
                        <div class="flex flex-wrap gap-1.5">
                          <button
                            v-for="lv in LIQUID_VARS"
                            :key="lv.value"
                            type="button"
                            class="inline-flex min-h-11 items-center rounded-full bg-grass-3 px-3 py-1 text-xs font-mono text-grass-11 transition-colors hover:bg-grass-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                            :title="lv.value"
                            @click="
                              insertLiquidVar(idx, 'message_template', lv.value)
                            "
                          >
                            {{ lv.label }}
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- On failure -->
                    <div>
                      <label
                        class="block text-xs font-medium text-n-slate-11 mb-2"
                      >
                        {{
                          t('PIPELINE.SEQUENCE_DEFINITIONS.FIELD_ON_FAILURE')
                        }}
                      </label>
                      <div class="flex flex-wrap gap-2">
                        <label
                          v-for="opt in ['continue', 'pause', 'cancel']"
                          :key="opt"
                          class="flex min-h-11 cursor-pointer items-center gap-2"
                        >
                          <input
                            v-model="step.on_failure"
                            type="radio"
                            :name="`step_${idx}_on_failure`"
                            :value="opt"
                            class="accent-n-brand"
                          />
                          <span class="text-xs text-n-slate-12">
                            {{
                              t(
                                `PIPELINE.SEQUENCE_DEFINITIONS.OPTION_${opt.toUpperCase()}`
                              )
                            }}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </template>

                <!-- ── WhatsApp Media fields ────────────────────────────── -->
                <template v-else-if="step.activity_type === 'whatsapp_media'">
                  <div class="space-y-3 pt-1 border-t border-n-weak">
                    <!-- Attachment type + Inbox row -->
                    <div class="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label
                          :for="`sequence-step-${idx}-attachment-type`"
                          class="block text-xs font-medium text-n-slate-11 mb-1"
                        >
                          {{
                            t(
                              'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_ATTACHMENT_TYPE'
                            )
                          }}
                          <span class="text-ruby-11">*</span>
                        </label>
                        <select
                          :id="`sequence-step-${idx}-attachment-type`"
                          v-model="step.attachment_type"
                          :name="`sequence-step-${idx}-attachment-type`"
                          class="!mb-0 min-h-11 w-full rounded-md border bg-n-solid-3 py-2 pl-3 pr-10 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                          :class="
                            errors[`step_${idx}_attachment_type`]
                              ? 'border-ruby-8'
                              : 'border-n-weak'
                          "
                        >
                          <option value="" disabled>—</option>
                          <option value="image">
                            {{
                              t('PIPELINE.SEQUENCE_DEFINITIONS.OPTION_IMAGE')
                            }}
                          </option>
                          <option value="audio">
                            {{
                              t('PIPELINE.SEQUENCE_DEFINITIONS.OPTION_AUDIO')
                            }}
                          </option>
                          <option value="video">
                            {{
                              t('PIPELINE.SEQUENCE_DEFINITIONS.OPTION_VIDEO')
                            }}
                          </option>
                          <option value="document">
                            {{
                              t('PIPELINE.SEQUENCE_DEFINITIONS.OPTION_DOCUMENT')
                            }}
                          </option>
                        </select>
                        <p
                          v-if="errors[`step_${idx}_attachment_type`]"
                          class="text-xs text-ruby-11 mt-1 flex items-center gap-1"
                        >
                          <FluentIcon icon="error-circle" size="11" />
                          {{ errors[`step_${idx}_attachment_type`] }}
                        </p>
                      </div>
                      <div>
                        <label
                          :for="`sequence-step-${idx}-media-inbox`"
                          class="block text-xs font-medium text-n-slate-11 mb-1"
                        >
                          {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FIELD_INBOX') }}
                        </label>
                        <select
                          :id="`sequence-step-${idx}-media-inbox`"
                          v-model="step.inbox_id"
                          :name="`sequence-step-${idx}-media-inbox`"
                          class="!mb-0 min-h-11 w-full rounded-md border border-n-weak bg-n-solid-3 py-2 pl-3 pr-10 text-sm text-n-slate-12 transition-colors focus:border-n-brand focus:outline-none focus:ring-2 focus:ring-n-brand/20"
                        >
                          <option value="">
                            {{
                              t(
                                'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_INBOX_PLACEHOLDER'
                              )
                            }}
                          </option>
                          <option
                            v-for="inbox in whatsappInboxes"
                            :key="inbox.id"
                            :value="inbox.id"
                          >
                            {{ inbox.name }}
                          </option>
                        </select>
                      </div>
                    </div>

                    <!-- Attachment URL -->
                    <div>
                      <label
                        :for="`sequence-step-${idx}-attachment-url`"
                        class="block text-xs font-medium text-n-slate-11 mb-1"
                      >
                        {{
                          t(
                            'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_ATTACHMENT_URL'
                          )
                        }}
                        <span class="text-ruby-11">*</span>
                      </label>
                      <input
                        :id="`sequence-step-${idx}-attachment-url`"
                        v-model="step.attachment_url"
                        :name="`sequence-step-${idx}-attachment-url`"
                        type="url"
                        class="min-h-11 w-full px-3 py-2 rounded-md border bg-n-solid-3 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors"
                        :class="
                          errors[`step_${idx}_attachment_url`]
                            ? 'border-ruby-8'
                            : 'border-n-weak'
                        "
                        :placeholder="
                          t(
                            'PIPELINE.SEQUENCE_DEFINITIONS.FIELD_WEBHOOK_URL_PLACEHOLDER'
                          )
                        "
                      />
                      <p
                        v-if="errors[`step_${idx}_attachment_url`]"
                        class="text-xs text-ruby-11 mt-1 flex items-center gap-1"
                      >
                        <FluentIcon icon="error-circle" size="11" />
                        {{ errors[`step_${idx}_attachment_url`] }}
                      </p>
                    </div>

                    <!-- Caption textarea with liquid chips -->
                    <div>
                      <label
                        :for="`sequence-step-${idx}-caption`"
                        class="block text-xs font-medium text-n-slate-11 mb-1"
                      >
                        {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FIELD_CAPTION') }}
                      </label>
                      <textarea
                        :id="`sequence-step-${idx}-caption`"
                        :ref="el => setTextareaRef(idx, 'caption', el)"
                        v-model="step.caption"
                        :name="`sequence-step-${idx}-caption`"
                        rows="2"
                        class="w-full px-3 py-2 rounded-md border border-n-weak bg-n-solid-3 text-sm text-n-slate-12 placeholder:text-n-slate-9 focus:outline-none focus:border-n-brand focus:ring-2 focus:ring-n-brand/20 transition-colors resize-y"
                      />
                      <div class="mt-2">
                        <p class="text-xs text-n-slate-10 mb-1.5">
                          {{
                            t(
                              'PIPELINE.SEQUENCE_DEFINITIONS.VARIABLES_AVAILABLE'
                            )
                          }}
                        </p>
                        <div class="flex flex-wrap gap-1.5">
                          <button
                            v-for="lv in LIQUID_VARS"
                            :key="lv.value"
                            type="button"
                            class="inline-flex min-h-11 items-center rounded-full bg-grass-3 px-3 py-1 text-xs font-mono text-grass-11 transition-colors hover:bg-grass-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand"
                            :title="lv.value"
                            @click="insertLiquidVar(idx, 'caption', lv.value)"
                          >
                            {{ lv.label }}
                          </button>
                        </div>
                      </div>
                    </div>

                    <!-- On failure -->
                    <div>
                      <label
                        class="block text-xs font-medium text-n-slate-11 mb-2"
                      >
                        {{
                          t('PIPELINE.SEQUENCE_DEFINITIONS.FIELD_ON_FAILURE')
                        }}
                      </label>
                      <div class="flex flex-wrap gap-2">
                        <label
                          v-for="opt in ['continue', 'pause', 'cancel']"
                          :key="opt"
                          class="flex min-h-11 cursor-pointer items-center gap-2"
                        >
                          <input
                            v-model="step.on_failure"
                            type="radio"
                            :name="`step_${idx}_on_failure`"
                            :value="opt"
                            class="accent-n-brand"
                          />
                          <span class="text-xs text-n-slate-12">
                            {{
                              t(
                                `PIPELINE.SEQUENCE_DEFINITIONS.OPTION_${opt.toUpperCase()}`
                              )
                            }}
                          </span>
                        </label>
                      </div>
                    </div>
                  </div>
                </template>
              </div>
            </div>

            <!-- Empty state -->
            <button
              v-if="!form.steps.length"
              type="button"
              class="w-full py-10 px-6 rounded-xl border-2 border-dashed border-n-weak hover:border-n-brand hover:bg-n-brand/5 transition-colors flex flex-col items-center gap-2 text-n-slate-11 hover:text-n-brand"
              @click="addStep"
            >
              <div
                class="flex items-center justify-center w-12 h-12 rounded-xl bg-n-alpha-2"
              >
                <FluentIcon icon="add" size="20" />
              </div>
              <span class="text-sm font-medium">
                {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_ADD_FIRST') }}
              </span>
            </button>

            <!-- Add another step -->
            <button
              v-else
              type="button"
              class="w-full py-3 px-4 rounded-xl border-2 border-dashed border-n-weak hover:border-n-brand hover:bg-n-brand/5 transition-colors flex items-center justify-center gap-2 text-sm font-medium text-n-slate-11 hover:text-n-brand"
              @click="addStep"
            >
              <FluentIcon icon="add" size="14" />
              {{ t('PIPELINE.SEQUENCE_DEFINITIONS.FORM_STEP_ADD') }}
            </button>
          </div>
        </section>
      </div>
    </div>

    <ConfirmationModal
      ref="discardConfirmationRef"
      :title="t('PIPELINE.SEQUENCE_DEFINITIONS.UNSAVED_CHANGES_TITLE')"
      :description="t('PIPELINE.SEQUENCE_DEFINITIONS.UNSAVED_CHANGES_CONFIRM')"
      :confirm-label="
        t('PIPELINE.SEQUENCE_DEFINITIONS.UNSAVED_CHANGES_DISCARD')
      "
      :cancel-label="
        t('PIPELINE.SEQUENCE_DEFINITIONS.UNSAVED_CHANGES_KEEP_EDITING')
      "
    />
  </div>
</template>
